/**
 * THE RULED LIMITS, mirrored from the contract so the form can say them before a person types.
 *
 * Two numbers exist for most of these fields and only ONE of them is ever shown.
 *   - The RULED limit is a GRAPHEME count — one emoji is one character, the way a person counts.
 *     That is the number in the field's description, and it is the one to show a user.
 *   - The database `CHECK` sits roughly ten per cent higher purely as a backstop, and those
 *     numbers are deliberately NOT published: telling a client the backstop invites it to send
 *     text the API refuses.
 *
 * AND THERE IS A THIRD NUMBER, which is easy to miss and is stated in `updateOwnProfile`'s own
 * description: JSON Schema's `maxLength` counts CODE POINTS, and "where the two differ the numeric
 * `maxLength` is the STRICTER check, not the looser one". One family emoji is one character to a
 * person and eleven code points to `maxLength`. So a value inside its published grapheme limit can
 * still be refused by the machine check. This module therefore measures BOTH and the validator
 * refuses on either — the grapheme count is what the counter shows, the code-point count is what
 * actually binds.
 *
 * Every number below is read from `docs/design/07-api/authors-api.yaml`'s `OwnProfileUpdate`,
 * `AuthorTag`, `SocialLink`, `UploadClaim`, `ApplicationSubmitRequest` and the
 * `checkSlugAvailability` parameter. None is remembered.
 */

/** The ruled maximum for a single-value text field, in graphemes. */
export const LIMITS = {
  /** No `maxLength` is published for displayName on OwnProfileUpdate — see the findings. */
  displayName: { max: undefined, min: undefined },
  slug: { min: 3, max: 60 },
  headline: { max: 70 },
  /** A BAND with a real minimum: 200 to 1200, or empty. There is no in-between value. */
  bioLong: { min: 200, max: 1200 },
  bioShort: { max: 250 },
  about: { max: 2200 },
  experience: { max: 2200 },
  approach: { max: 2200 },
  education: { max: 2200 },
  pronouns: { max: 40 },
  languageCode: { min: 2, max: 8 },
  tagText: { min: 2, max: 40 },
  linkKind: { max: 32 },
  linkUrl: { min: 4, max: 500 },
  categoryCode: { max: 64 },
  privateEmail: { max: 320 },
  availabilityNote: { max: 500 },
  intakeId: { min: 8, max: 64 },
  credentialFileName: { min: 1, max: 255 },
  acceptedTermsVersion: { min: 1, max: 64 },
} as const;

/**
 * The ruled shape for the public page address: lowercase Latin letters, digits and single
 * hyphens. It is stated in prose on `checkSlugAvailability` and in `04-schema.md`, and — unlike
 * the article slug, which carries the identical regex as a machine `pattern` — the AUTHOR slug
 * publishes no `pattern` anywhere in the spec. So this constant is a hand-mirror of a prose rule,
 * which is exactly the kind of copy that drifts. It is one of the findings.
 */
export const SLUG_SHAPE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** The public address the author is choosing, spelled out so they can see what they are picking. */
export const PUBLIC_PAGE_PREFIX = 'moonx.app/authors/';

/**
 * The three image types the platform accepts, listed explicitly.
 *
 * NEVER Mantine's bundled `IMAGE_MIME_TYPES` here. SVG is REJECTED server-side by magic bytes,
 * and a control that lets somebody drop one only to have the claim fail afterwards has misled
 * them. Validation is by magic bytes and not by extension, so this list is a courtesy to the
 * person picking a file, never a guarantee.
 */
export const AVATAR_ACCEPT = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** Identity evidence. PDFs are accepted and are not parsed. */
export const CREDENTIAL_ACCEPT = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

/**
 * THE UPLOAD CEILING, IN BYTES, AND A FILE HAS TO BE STRICTLY SMALLER. Two rules bound the same
 * number and the stricter one binds:
 *   - the quarantine rule admits a write only while "request.resource.size < 20 * 1024 * 1024"
 *     (docs/design/adr/0010-firebase-integration.md) - `<`, so exactly this many bytes is out;
 *   - both claims refuse MORE than the seeded `max_upload_bytes`, 20971520 on the avatar's row and
 *     on the credential's (prisma/migrations/0003_lookups_and_seeds/migration.sql).
 * So a file is refused at `>=` this, never at `>`.
 *
 * WHY THE FORM CHECKS IT AT ALL. In production Storage answers an oversized write with a bare 403,
 * before any claim runs, and a 403 says nothing an author can act on. Both upload hooks
 * (../data/useUpload.ts) refuse at this size before a byte moves, where the refusal can say why.
 * The editor keeps the same number for the same rule (`MAX_IMAGE_BYTES`,
 * routes/articles/editor/use-article-image-upload.ts); a route is not a place a feature imports.
 */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/**
 * The nine per-field visibility switches, and their ruled defaults. Everything is public by
 * default EXCEPT the timezone. `displayName`, `avatar`, `bioShort` and `about` have NO switch —
 * that absence is the contract (`FieldVisibility`'s properties), not an oversight. Design 25 added
 * the last two (T25, T27); the time zone's switch is no longer drawn - "Time zone" left the form for
 * "Primary location", and its value and switch stay on file for the apps that read them. Their names
 * are the `application` catalogue's `visibility.<key>`.
 */
export const VISIBILITY_FIELDS = [
  { key: 'headline', defaultPublic: true },
  { key: 'bioLong', defaultPublic: true },
  { key: 'experience', defaultPublic: true },
  { key: 'approach', defaultPublic: true },
  { key: 'education', defaultPublic: true },
  { key: 'pronouns', defaultPublic: true },
  { key: 'timezone', defaultPublic: false },
  { key: 'yearsOfPractice', defaultPublic: true },
  { key: 'primaryLocation', defaultPublic: true },
] as const;

export type VisibilityKey = (typeof VISIBILITY_FIELDS)[number]['key'];

/** The switches whose field is text - every one but design 25's number and place. */
export type TextVisibilityKey = Exclude<VisibilityKey, 'yearsOfPractice' | 'primaryLocation'>;

/** "Years of practice" (design 25, 3.3): a whole number, 0 to 80, or nothing. */
export const YEARS_OF_PRACTICE = { min: 0, max: 80 } as const;

/**
 * The nine `social_link_kind` rows, seeded in `04-schema.md`. THIS IS A HAND-COPY OF A MANAGED
 * LOOKUP AND IT SHOULD NOT BE. `SocialLink.linkKind`'s own description says "Adding one is a row,
 * so this is not a closed enum here" — but no operation on any of the five specs serves this
 * vocabulary, so a client has nowhere to read it from. Derived by script over all five specs:
 * zero operations mention social link kinds. It is one of the findings.
 */
export const SOCIAL_LINK_KINDS = [
  { code: 'website', label: 'Website' },
  { code: 'instagram', label: 'Instagram' },
  { code: 'facebook', label: 'Facebook' },
  { code: 'youtube', label: 'YouTube' },
  { code: 'tiktok', label: 'TikTok' },
  { code: 'telegram', label: 'Telegram' },
  { code: 'pinterest', label: 'Pinterest' },
  { code: 'linkedin', label: 'LinkedIn' },
  { code: 'x', label: 'X' },
] as const;

/**
 * WHOLE-SET REPLACEMENT for `expertise`, `tags` and `links`.
 *
 * The contract fixes the SCALAR semantics precisely — absent is left alone, explicit null clears —
 * and says NOTHING about the three arrays. There is no per-item delete route anywhere on the
 * surface, so if sending the array merged, an author could never remove a link, a tag or a
 * subject. Replacement is therefore the only reading under which removal is possible at all, and
 * it matches the composite primary keys the tables carry. This constant exists so the assumption
 * is written down once and is easy to find if the owner rules the other way.
 */
export const ARRAY_UPDATE_SEMANTICS = 'replace-whole-set' as const;

/**
 * "Topics you know best" - the author's tags - stop at eight (design 24, T25, the prototype's own
 * shape). No `maxItems` is published on `tags` (derived: `maxItems` occurs twice in the whole of
 * authors-api.yaml and neither occurrence is on these arrays), so the contract does not bound it:
 * this is a cabinet limit. The field stops a ninth and says so; an author who already holds more
 * (the import brought them) keeps them, and the save is never refused for it.
 */
export const TOPICS_MAX = 8;

/**
 * "Your main areas" - the expertise chips - stop at three, as the prototype's "Primary topics" do
 * (design 24, T25). A cabinet limit too: the contract bounds `expertise` with nothing, so an author
 * already holding more keeps them and only a new pick past three is stopped.
 */
export const EXPERTISE_MAX = 3;

const graphemeSegmenter =
  typeof Intl !== 'undefined' && 'Segmenter' in Intl
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : undefined;

/** What the person counts: one emoji is one. This is the number a counter shows. */
export function graphemeLength(value: string): number {
  if (graphemeSegmenter === undefined) {
    // Code points, which is closer to a person's count than UTF-16 units are.
    return Array.from(value).length;
  }
  let count = 0;
  for (const _segment of graphemeSegmenter.segment(value)) count += 1;
  return count;
}

/** What `maxLength` counts, and therefore what actually binds first. */
export function codePointLength(value: string): number {
  return Array.from(value).length;
}
